import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import type { Category, CreateCategoryBody, UpdateCategoryBody } from '@klopotun/contracts';
import { categoryQueries } from '../queries/category-queries.js';
import { createCategory } from '../requests/create-category.js';
import { updateCategory } from '../requests/update-category.js';

export type SaveCategoryInput =
  | { mode: 'create'; body: CreateCategoryBody }
  | { mode: 'update'; id: string; body: UpdateCategoryBody };

const saveCategory = (input: SaveCategoryInput): Promise<Category> => {
  if (input.mode === 'create') {
    return createCategory(input.body);
  }
  return updateCategory(input.id, input.body);
};

const withCategory = (
  categories: Category[] | undefined,
  category: Category,
): Category[] | undefined => {
  if (categories === undefined || categories.some((known) => known.id === category.id)) {
    return categories;
  }

  return [...categories, category];
};

export const useSaveCategoryMutation = (): UseMutationResult<
  Category,
  Error,
  SaveCategoryInput
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: saveCategory,
    onSuccess: (category, input) => {
      // A picker that selects the new category the moment it exists must find it among its
      // options before the refetch lands; otherwise the select shows nothing for a beat.
      if (input.mode === 'create') {
        queryClient.setQueriesData<Category[]>(
          { queryKey: categoryQueries.lists() },
          (categories) => withCategory(categories, category),
        );
      }

      return queryClient.invalidateQueries({ queryKey: categoryQueries.lists() });
    },
  });
};
